import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Typography, Stack, CircularProgress, Alert } from '@mui/material';
import { Note as NoteIcon } from '@mui/icons-material';
import useThemeStyles from '../../../../../hooks/useThemeStyles';
import useChapterNotes from '../hooks/useChapterNotes';
import ChapterSelect from '../components/ChapterSelect';
import NoteQuickAddForm from '../components/NoteQuickAddForm';
import NoteEditItem from '../components/NoteEditItem';

/**
 * Chapter-aware notes panel. Follows the chapter currently in view unless the
 * user pins a specific chapter with the selector.
 */
const NotesModeBody = ({ projectId, chapters }) => {
  const { colors } = useThemeStyles();
  const activeChapterId = useSelector((state) => state.projects.activeChapterId);
  const [pinnedChapterId, setPinnedChapterId] = useState('');

  const chapterId = useMemo(() => {
    if (pinnedChapterId) return pinnedChapterId;
    if (activeChapterId) return String(activeChapterId);
    const first = chapters[0];
    return first ? String(first.id || first._id) : '';
  }, [pinnedChapterId, activeChapterId, chapters]);

  const currentChapter = chapters.find((ch) => String(ch.id || ch._id) === chapterId);
  const { notes, loading, error, createNote, updateNote, deleteNote } = useChapterNotes(
    projectId,
    chapterId
  );

  const handleCreate = async (fields) => {
    const response = await createNote(fields);
    return !response.error;
  };

  const handleSave = async (noteId, updates) => {
    const response = await updateNote(noteId, updates);
    return !response.error;
  };

  const handleDelete = async (noteId) => {
    const response = await deleteNote(noteId);
    return !response.error;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.sidebar.background,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border.subtle}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <ChapterSelect chapters={chapters} value={pinnedChapterId} onChange={setPinnedChapterId} />
        {currentChapter && (
          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
            Notes for “{currentChapter.name || 'Untitled Chapter'}”
          </Typography>
        )}
        <NoteQuickAddForm onSubmit={handleCreate} disabled={!chapterId} />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} sx={{ color: colors.interactive.primary }} />
          </Box>
        )}
        {!loading && notes.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '160px',
              color: colors.text.secondary,
              textAlign: 'center',
            }}
          >
            <NoteIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.5 }} />
            <Typography variant="body2">No notes for this chapter yet</Typography>
          </Box>
        )}
        {!loading && notes.length > 0 && (
          <Stack spacing={1.5}>
            {notes.map((note) => (
              <NoteEditItem
                key={note._id || note.id}
                note={note}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

NotesModeBody.propTypes = {
  projectId: PropTypes.string,
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

NotesModeBody.defaultProps = {
  projectId: null,
  chapters: [],
};

export default NotesModeBody;
